import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
//eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { filter, find, get, isEqual, isUndefined, map, some } from 'lodash';
import { BenchmarkService, DateFormatterService, PaginatorComponent, UNIT } from '@nrwl/common-lib';
import { DomSanitizer } from '@angular/platform-browser';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IRow {}

export interface ITableData {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: Array<any>;
  data: Array<IRow>;
  selections?: {
    preselectAll: boolean;
  };
}

@Component({
  selector: 'nrwl-view-table',
  templateUrl: './view-table.component.html',
  styleUrls: ['./view-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewTableComponent implements OnInit, AfterViewInit, OnDestroy {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() sendRowAction?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() clicked?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() sendPageSize?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() hovered?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() emitSelectAllEvent?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() dataSource: BehaviorSubject<any>;
  // ID of an item which need to be selected
  @Input() selectedRowIndex?: BehaviorSubject<number>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() options?: any;
  @Input() count: number;
  @Input() paginatorOptions?: BehaviorSubject<boolean>;
  @Input() itemMenu?: ElementRef;
  // exposes what has been selected in action/selection column within table.
  @Output() selectionsChanged: EventEmitter<
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    SelectionChange<any>
  > = new EventEmitter();
  @Output() sortChange: EventEmitter<any> = new EventEmitter();
  @ViewChild('paginator', { static: true }) paginatorComponent: PaginatorComponent;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @Input() defaultPageSize: number;
  @Input() totalResult: number;
  @Input() defaultPageSizeOptions: number[];
  @Input() hidePageSizer?: true;
  @Input() resultVisible: false;
  @Input() noDataFoundMsg: string;

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortedData = new MatTableDataSource<any>([]);

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  selection: SelectionModel<any> = new SelectionModel<any>(true, [], true);

  dsSubscription: Subscription;

  paginatorOptionsSubscription: Subscription;
  selectedItemSubscription: Subscription;
  tableSelectionsSubscription: Subscription;
  isSelectedAll = null;
  showPaginator = false;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any[];
  displayedColumnsIds: string[];
  selectedID = 0;
  currentRowId = 0;
  UNIT = UNIT;
  staticPageSizes = [10, 20, 50];
  dtFormatLocal: Intl.DateTimeFormat = new Intl.DateTimeFormat('default', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    hourCycle: 'h23'
  });
  dtFormatUTC: Intl.DateTimeFormat = new Intl.DateTimeFormat('default', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    hourCycle: 'h23',
    timeZone: 'UTC',
    timeZoneName: 'short'
  });
  dtFormatLocalWithSeconds: Intl.DateTimeFormat = new Intl.DateTimeFormat('default', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    hourCycle: 'h23'
  });
  dtFormatUTCWithSeconds: Intl.DateTimeFormat = new Intl.DateTimeFormat('default', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    hourCycle: 'h23',
    timeZone: 'UTC',
    timeZoneName: 'short'
  });

  @Input()
  set searchText(text: string) {
    this.sortedData.filter = text;
  }

  constructor(
    private readonly dateFormatterService: DateFormatterService,
    private sanitizer: DomSanitizer,
    private readonly benchmarkService: BenchmarkService
  ) {}

  ngOnInit() {
    if (!this.defaultPageSize) {
      this.defaultPageSize = 10;
    }
    if (this.options && this.options.defaultSorting) {
      this.sort.sort({
        id: this.options.defaultSorting.id,
        start: this.options.defaultSorting.start
      } as MatSortable);
    }
    this.benchmarkService.registerPageEvent('tableRendering');
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.dsSubscription = this.dataSource.subscribe((dataItem: any) => {
      if (dataItem) {
        this.sortedData.paginator = this.paginatorComponent.paginator;
        this.sortedData.sort = this.sort;
        this.sortedData.data = dataItem.data;
        this.sort.sortables.forEach(value => {
          this.sort.deregister(value);
        });
        this.columns = dataItem.column;
        this.sortedData.sortingDataAccessor = this.getSortingDataAccessor(this.columns);
        this.sortedData.sortData(this.sortedData.filteredData, this.sortedData.sort);
        this.displayedColumnsIds = this.getDisplayedColumnsIds();
        if (dataItem.selections && dataItem.selections.preselectAll) {
          this.masterToggle();
        }
      }
    });
    this.paginatorOptionsSubscription = this.paginatorOptions.subscribe((data: boolean) => {
      this.showPaginator = data;
    });
    if (this.selectedRowIndex) {
      this.selectedItemSubscription = this.selectedRowIndex.subscribe(appId => {
        if (appId !== 0) {
          this.selectedID = appId;
          this.selectItemBasedOnIndex(appId);
        }
      });
    }
    this.tableSelectionsSubscription = this.selection.changed.subscribe(item =>
      this.selectionsChanged.emit(item)
    );
  }

  ngOnDestroy() {
    if (this.dsSubscription) {
      this.dsSubscription.unsubscribe();
    }
    if (this.paginatorOptionsSubscription) {
      this.paginatorOptionsSubscription.unsubscribe();
    }
    if (this.selectedItemSubscription) {
      this.selectedItemSubscription.unsubscribe();
    }
    if (this.tableSelectionsSubscription) {
      this.tableSelectionsSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange.subscribe(() => {
      if (this.sortChange) {
        this.sortChange.emit(this.sort);
      }
      this.paginatorComponent.paginator.pageIndex = 0;
      this.paginatorComponent.paginator._changePageSize(this.paginatorComponent.paginator.pageSize);
      this.sortedData.sortData(this.sortedData.filteredData, this.sortedData.sort);
      this.sortedData.sortingDataAccessor = this.getSortingDataAccessor(this.columns);
    });
    this.sortedData.filterPredicate = this.getFilterPredicate(this.columns);
    this.benchmarkService.logPageEventTime('Table rendering', 'tableRendering');
  }

  get hideActionsColumn(): boolean {
    if (!this.options) {
      return false;
    }
    return this.options.hideActionsColumn;
  }

  nextPage(event: PageEvent): void {
    this.sortedData.paginator.pageIndex = event.pageIndex;
    this.sendPageSize.next(event);
  }

  getDisplayedColumnsIds(): string[] {
    const result = this.columns.map(c => c.id);
    if (!this.hideActionsColumn) {
      result.unshift('select');
    }
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  selectItemBasedOnIndex(id: any, option?: any): void {
    //Select only if id exists, otherwise all rows are selected
    if (!isUndefined(id)) {
      this.sortedData.data.forEach((row, index) => {
        if (isEqual(row.id, id)) {
          this.onRowClick(row, index, option);
        }
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  onActionClick(id: any, action?: any, rowId?: any) {
    this.sendRowAction.next({ id: id, action: action, rowId: rowId });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  onRowClick(row: any, index: number, option?: any): void {
    this.clicked.next({ data: row, option: option });
    if (this.selectedID !== 0) {
      this.currentRowId = row.id;
      this.changePaginationIndex(index);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  onRowHover(row: any, action: string, event = null, index = null) {
    this.hovered.next({ row, action, event, index });
  }

  formatDate(date: number): string {
    return this.dateFormatterService.getDate(date);
  }

  formatTime(date: number): string {
    return this.dateFormatterService.getTime(date);
  }

  changePaginationIndex(index: number): void {
    this.paginatorComponent.paginator.pageIndex = Math.floor(
      index / this.paginatorComponent.paginator.pageSize
    );
    this.sortedData.paginator = this.paginatorComponent.paginator;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    let numRows;
    // eslint-disable-next-line no-prototype-builtins
    if (this.sortedData.data.some(data => data.hasOwnProperty('isDisabled'))) {
      numRows = this.sortedData.data.filter(x => !x.isDisabled).length;
    } else {
      numRows = this.sortedData.data.length;
    }
    return numSelected === numRows;
  }

  isCheckboxColumnDisabled(): boolean {
    return !this.sortedData.data.some(x => !x.isDisabled);
  }
  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.sortedData.data.forEach(row => {
          if (!row?.isDisabled) {
            this.selection.select(row.id);
          }
        });
    this.emitSelectAllEvent.emit(this.isAllSelected() ? 'all' : null);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  getSortingDataAccessor(columns: any[]) {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    return (item: any, property: any) => {
      const type = get(find(columns, { id: property }), 'type');

      let result = this.getSortItem(property, type, item);
      if (typeof result === 'string') {
        result = result.toLocaleLowerCase();
      }

      return result;
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  getFilterPredicate(columns: any[]): any {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    return (item: any, property: any) => {
      return some(columns, column => {
        let value = get(item, column.id, '');
        if (column.type === 'badge-text') {
          value = get(item, `${column.id}.value`, '');
        }
        return value.toString().toLowerCase().indexOf(property.toLowerCase()) > -1;
      });
    };
  }

  isSortingDisabled(): boolean {
    return get(this.options, 'disableSort', false);
  }

  addTitleOnTruncate(ev: MouseEvent): void {
    const currentTarget = <HTMLElement>ev.currentTarget;
    if (currentTarget && currentTarget.offsetWidth < currentTarget.scrollWidth) {
      currentTarget.setAttribute('title', currentTarget.innerText);
    } else {
      currentTarget.removeAttribute('title');
    }
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getArrayString(array: Array<any>, key: string): string {
    return map(
      filter(array, item => {
        return item[key];
      }),
      key
    ).join(', ');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  onChange(row: any): void {
    this.selection.toggle(row.id);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  isChecked(row: any): boolean {
    return this.selection.isSelected(row.id);
  }

  formatDateTime(date: Date, formatter: Intl.DateTimeFormat): string {
    if (date) {
      date = new Date(date);
      return formatter.format(date).replace(', ', ' ');
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  formatKpiValue(kpiValue, kpiName: string): string {
    function roundOff(value: number, decimals: number) {
      const shift = Math.pow(10, decimals);
      return Math.round(value * shift + 0.00000000000001) / shift;
    }
    const val = Number(kpiValue);
    switch (kpiName) {
      case 'AVAILABILITY_PCT':
      case 'LOSS_PCT':
      case 'UNAVAILABLE_LOSS_PCT':
        return roundOff(val, 2) + ' %';
      case 'JITTER_AVG':
      case 'JITTER_MAX':
      case 'LATENCY_AVG':
      case 'LATENCY_MAX':
        return roundOff(val, 3) + ' ms';
      default:
        return `${roundOff(val, 0)}`;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  expandRow(element: any): void {
    element.isExpand = !element.isExpand;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  getSvgContent(svg: string): any {
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSingleSvgContent(svg: string): any {
    if (svg) {
      return this.sanitizer.bypassSecurityTrustHtml(svg);
    }
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  isExpand(element: any): boolean {
    return element.isExpand;
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  disableDoAction(element: any): boolean {
    if (element && element?.disableAction) {
      return true;
    }
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getSortItem(columnID, columnType, item): any {
    switch (columnType) {
      case 'label':
      case 'status-preview':
        return get(item, columnID, [])
          .map(item => item.label)
          .join(', ');
      case 'icon-text':
      case 'icon-text-column':
      case 'text-icon-label':
        return (
          get(item, columnID + '.label')?.trim() + get(item, columnID + '.sublabel', '')?.trim()
        );
      case 'mat-icon':
        return get(item, columnID + '.icon');
      case 'progress':
        return get(item, columnID + '.text');
      case 'status-icon':
      case 'status-icon-label':
        return get(item, columnID + '.label');
      case 'label-a':
        if (item.active) {
          return get(item, columnID, [])
            .map(item => item.label)
            .join(', ');
        }
        return;
      case 'badge-preview':
        return get(item, columnID, [])
          .map(item => item.result)
          .join();
      case 'badge-text':
      case 'badge-score':
        return get(item, columnID).value;
      case 'text': {
        switch (columnID) {
          case 'avg_bits_sec':
            return get(item, `${columnID}_bitspersecond`) || get(item, `${columnID}_rowcount`);
          case 'avg':
          case 'max':
            return (
              get(item, `${columnID}_bitspersecond`) ||
              get(item, `${columnID}_uniqueserveraddcount`) ||
              get(item, `${columnID}_uniqueclientaddcount`) ||
              get(item, `${columnID}_rowcount`)
            );
          case 'pct':
          case 'in_pct':
            // eslint-disable-next-line no-case-declarations
            const paresNumRegex = new RegExp(/[\d|,|.|e|E|+]+/g);
            // eslint-disable-next-line no-case-declarations
            const parsed: Array<string> = get(item, columnID).match(paresNumRegex);
            if (parsed && parsed.length > 0) {
              return Number(parsed[0]);
            }
            break;
          case 'ip1':
          case 'ip2':
          case 'ip1_classa':
          case 'ip1_classb':
          case 'ip1_classc':
          case 'ip2_classa':
          case 'ip2_classb':
          case 'ip2_classc':
            // eslint-disable-next-line no-case-declarations
            const ip = get(item, columnID);
            if (ip) {
              return (
                ip.split('.').reduce(function (ipInt, octet) {
                  return (ipInt << 8) + parseInt(octet, 10);
                }, 0) >>> 0
              );
            }
        }
        const value = get(item, columnID);
        return value && typeof value === 'string' ? value.toLocaleLowerCase() : value;
      }
      case 'text-with-highlight':
        return get(item, columnID).value;
      case 'label-sublabel':
        return get(item, columnID)?.label;
      default:
        return get(item, columnID);
    }
  }
}
